/**
 * Mail catcher — thin `invoke` wrappers over the Rust `mail_catcher_*`
 * commands (src-tauri/src/mail_catcher/mod.rs) plus the two Rust→JS events.
 *
 * `@tauri-apps/api/core` is the ONE Tauri module shared code may import
 * statically (jest maps it, boardsStore/ftpStore precedent). Everything else
 * — the save dialog and the event bridge — is imported lazily inside the
 * function so the web build and every jsdom module graph that reaches this
 * file keep working (tests/tauriImportsAreLazy.test.js is the rule).
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';

/** Rust emits one per stored message (payload: Summary). */
export const MESSAGE_EVENT = 'mailcatcher:message';
/** Rust emits when the accept loop dies on its own (payload: Status). */
export const STATUS_EVENT = 'mailcatcher:status';
/**
 * Rust (`sync::engine::sync_switch_db`) emits when the account db FILE was
 * swapped — sign-in, sign-out, account switch — payload `{ accountId }`
 * (`_local` when signed out). Captured mail lives in that file
 * (08.09.2026: `mail_messages` in `users/<id>/apiboo.db`), so the store
 * resets on THIS, not on `auth:logout`: the swap is fire-and-forget from
 * syncAuthBridge.js and lands after the auth event.
 */
export const DB_SWITCHED_EVENT = 'sync:db-switched';

/**
 * Rows one `list` call asks for. Equal to `MAIL_CATCHER_KEEP_MAX` (5000) on
 * purpose: v1 has NO paging — `load()` fetches once at offset 0 — so a
 * smaller limit would count messages the list can never show (fix round 1,
 * NB9). The Rust clamp is `store::LIST_LIMIT_MAX`, the same number. Paging
 * (offset + "load more") is a v2 item, deliberately.
 */
export const DEFAULT_LIST_LIMIT = 5000;

export const start = (port) => invoke('mail_catcher_start', { port });
export const stop = () => invoke('mail_catcher_stop');
export const status = () => invoke('mail_catcher_status');
export const list = ({ query = '', limit = DEFAULT_LIST_LIMIT, offset = 0 } = {}) =>
  invoke('mail_catcher_list', { query: query ? query : null, limit, offset });
export const get = (id) => invoke('mail_catcher_get', { id });
export const raw = (id) => invoke('mail_catcher_raw', { id });
export const remove = (ids) => invoke('mail_catcher_delete', { ids });
export const clear = () => invoke('mail_catcher_clear');
export const setKeep = (keep) => invoke('mail_catcher_set_keep', { keep });

/** The fail-closed bind error names the port and says "already in use". */
export const isPortInUseError = (err) => /already in use/i.test(String(err ?? ''));

async function pickSavePath(filename) {
  const { save } = await import('@tauri-apps/plugin-dialog');
  return save({ defaultPath: filename });
}

/**
 * Native save dialog → Rust writes the bytes (`mail_catcher_save_attachment`),
 * so no fs-plugin command and no capability grant is involved.
 * @returns {Promise<{saved: boolean, cancelled?: boolean, path?: string}>}
 */
export async function saveAttachment(id, index, filename) {
  const path = await pickSavePath(filename);
  if (!path) return { saved: false, cancelled: true };
  await invoke('mail_catcher_save_attachment', { id, index, path });
  return { saved: true, path };
}

export async function saveRaw(id, filename) {
  const path = await pickSavePath(filename);
  if (!path) return { saved: false, cancelled: true };
  await invoke('mail_catcher_save_raw', { id, path });
  return { saved: true, path };
}

/**
 * Subscribes to the three events. Returns synchronously; `unlisten()` is
 * safe at any time, including before the lazy import has resolved.
 */
export function subscribe({ onMessage, onStatus, onDbSwitched } = {}) {
  if (!isTauri()) return { unlisten() {} };
  let subs = null;
  let canceled = false;
  import('../utils/safeTauriEvent')
    .then(({ subscribeTauriEvent }) => {
      if (canceled) return;
      subs = [
        subscribeTauriEvent(MESSAGE_EVENT, (event) => onMessage?.(event?.payload)),
        subscribeTauriEvent(STATUS_EVENT, (event) => onStatus?.(event?.payload)),
        subscribeTauriEvent(DB_SWITCHED_EVENT, (event) => onDbSwitched?.(event?.payload)),
      ];
    })
    .catch(() => {});
  return {
    unlisten() {
      canceled = true;
      (subs || []).forEach((s) => s.unlisten());
      subs = null;
    },
  };
}
